import { isTrustedExtensionOrigin } from '@app/constants/config';

declare global {
  interface Window {
    isLoadedInWKWebView?: boolean;
    loadedFromMobile?: boolean;
    ReactNativeWebView?: {
      postMessage: (message: string) => void;
    };
    mobileApp?: { version?: string };
  }
}

type PlatformKey = 'ios' | 'android' | 'connect' | 'web';

const isLoadedFromMobile = (): boolean =>
  window?.isLoadedInWKWebView || window?.loadedFromMobile || false;

type WindowWithNativeAuthEnabled = typeof window & {
  FF_NATIVE_AUTH_ENABLED?: boolean;
};
const shouldUseNativeAuth = (): boolean =>
  isLoadedFromMobile() &&
  (window as WindowWithNativeAuthEnabled)?.FF_NATIVE_AUTH_ENABLED === true;

/**
 * Gets the version of the mobile app from user agent, or an empty string if not found.
 * @returns The version of the mobile app, or an empty string if not found.
 */
const getMobileAppVersion = (): string => {
  const explicit = (window as WindowWithNativeAuthEnabled)?.mobileApp?.version;
  if (explicit) return explicit;
  try {
    const ua = navigator?.userAgent || '';
    // Expect: HiMarleyMobileApp/1.2.3 (iOS; build:123)
    const match = ua.match(/HiMarleyMobileApp\/([\d.]+)\s*\(([^)]+)\)/i);
    if (match) {
      return match[1] || '';
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error getting mobile app version from user agent', error);
  }
  return '';
};

const isLoadedFromHiMarleyConnect = (): boolean => {
  const ancestor = window.location.ancestorOrigins?.[0];
  return (
    window.self !== window.top &&
    !!ancestor &&
    isTrustedExtensionOrigin(ancestor)
  );
};

function detectPlatform(): PlatformKey {
  try {
    if (isLoadedFromHiMarleyConnect()) {
      return 'connect';
    }
    if (isLoadedFromMobile()) {
      const isiOS =
        typeof window !== 'undefined' &&
        !!(window as Window & { webkit?: { messageHandlers?: unknown } }).webkit
          ?.messageHandlers;
      return isiOS ? 'ios' : 'android';
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error detecting platform', error);
  }
  return 'web';
}

export default isLoadedFromMobile;
export {
  isLoadedFromHiMarleyConnect,
  shouldUseNativeAuth,
  type PlatformKey,
  detectPlatform,
  getMobileAppVersion,
};
